/**
 * Minesweeper Error Handler
 *
 * Wraps ErrorHandlingService for game-specific error classification.
 * All user-facing messages go through NotificationService — never raw alert().
 *
 * Follows SudokuErrorHandler pattern.
 */

import {
  ErrorHandlingService,
  ErrorType,
  ErrorSeverity,
} from '../../services/ErrorHandlingService';
import {
  NotificationService,
  NotificationType,
} from '../../services/NotificationService';

export enum MinesweeperErrorType {
  INVALID_MOVE = 'INVALID_MOVE',
  GAME_ENDED = 'GAME_ENDED',
  GAME_LOGIC = 'GAME_LOGIC',
  CORRUPTED_DATA = 'CORRUPTED_DATA',
  SAVE_ERROR = 'SAVE_ERROR',
  LOAD_ERROR = 'LOAD_ERROR',
  UNKNOWN = 'UNKNOWN',
}

export interface MinesweeperError {
  type: MinesweeperErrorType;
  message: string;
  details?: unknown;
  timestamp: number;
  recoverable: boolean;
}

export class MinesweeperErrorHandler {
  private errorService: ErrorHandlingService;
  private notificationService: NotificationService;
  private errorLog: MinesweeperError[] = [];
  private readonly MAX_ERROR_LOG = 50;

  constructor(
    errorService: ErrorHandlingService,
    notificationService: NotificationService
  ) {
    this.errorService = errorService;
    this.notificationService = notificationService;
  }

  handleError(
    type: MinesweeperErrorType,
    message: string,
    details?: unknown,
    showNotification: boolean = true
  ): MinesweeperError {
    const error: MinesweeperError = {
      type,
      message,
      details,
      timestamp: Date.now(),
      recoverable: this.isRecoverable(type),
    };

    this.logError(error);

    const { errorType, severity } = this.classifyError(type);
    this.errorService.handleError(
      new Error(message),
      errorType,
      severity,
      { component: 'Minesweeper', additionalData: details as Record<string, unknown> },
      { showNotification: false }
    );

    if (showNotification) {
      this.showErrorNotification(error);
    }

    return error;
  }

  private classifyError(
    type: MinesweeperErrorType
  ): { errorType: ErrorType; severity: ErrorSeverity } {
    switch (type) {
      case MinesweeperErrorType.INVALID_MOVE:
      case MinesweeperErrorType.GAME_ENDED:
      case MinesweeperErrorType.GAME_LOGIC:
        return { errorType: ErrorType.GAME_LOGIC, severity: ErrorSeverity.LOW };

      case MinesweeperErrorType.CORRUPTED_DATA:
      case MinesweeperErrorType.SAVE_ERROR:
      case MinesweeperErrorType.LOAD_ERROR:
        return { errorType: ErrorType.STORAGE, severity: ErrorSeverity.MEDIUM };

      default:
        return { errorType: ErrorType.SYSTEM, severity: ErrorSeverity.MEDIUM };
    }
  }

  // Convenience methods
  handleGameLogicError(reason: string): MinesweeperError {
    return this.handleError(
      MinesweeperErrorType.GAME_LOGIC,
      `Game error: ${reason}`,
      { reason }
    );
  }

  handleCorruptedData(dataType: string, error: unknown): MinesweeperError {
    return this.handleError(
      MinesweeperErrorType.CORRUPTED_DATA,
      'Unable to load saved game. Starting new game.',
      { dataType, error },
      true
    );
  }

  handleSaveError(gameId: string, error: unknown): MinesweeperError {
    return this.handleError(
      MinesweeperErrorType.SAVE_ERROR,
      'Failed to save game. Your progress may be lost.',
      { gameId, error },
      true
    );
  }

  handleLoadError(gameId: string, error: unknown): MinesweeperError {
    return this.handleError(
      MinesweeperErrorType.LOAD_ERROR,
      'Failed to load game. Starting new game.',
      { gameId, error },
      true
    );
  }

  // Internal helpers
  private isRecoverable(type: MinesweeperErrorType): boolean {
    switch (type) {
      case MinesweeperErrorType.INVALID_MOVE:
      case MinesweeperErrorType.GAME_ENDED:
      case MinesweeperErrorType.GAME_LOGIC:
      case MinesweeperErrorType.CORRUPTED_DATA:
      case MinesweeperErrorType.SAVE_ERROR:
      case MinesweeperErrorType.LOAD_ERROR:
        return true;
      default:
        return false;
    }
  }

  private logError(error: MinesweeperError): void {
    this.errorLog.push(error);
    if (this.errorLog.length > this.MAX_ERROR_LOG) {
      this.errorLog.shift();
    }
  }

  private showErrorNotification(error: MinesweeperError): void {
    const notificationType = error.recoverable
      ? NotificationType.WARNING
      : NotificationType.ERROR;
    this.notificationService.addNotification({
      type: notificationType,
      title: 'Minesweeper Error',
      message: error.message,
      duration: 5000,
    });
  }

  getErrorLog(): MinesweeperError[] {
    return [...this.errorLog];
  }

  clearErrorLog(): void {
    this.errorLog = [];
  }
}
